import React, { Fragment, useState, useEffect, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import Config from '../../config';
import {
    Button,
    Card,
    Grid as GridBase,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CardContent,
    Toolbar,
    InputAdornment,
    Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Moment from 'react-moment';
import {AddressSelect, updateAddress} from '../../components/address';
import Helper, { getClassCodes, formatAddress, convertToPounds, triggerSubmit } from '../../helpers';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import TextFieldBase from '../../components/text_field';
import DocumentLink from '../../components/document_link';
import ContentLoader from '../../components/content_loader';
import axios from 'axios';
import FastSelect from '../../components/fast_suggest_field';
import { ItemAttributes, HeaderAttributes, LabelDialog } from '../documents/document_components';
import moment from 'moment';
import Auth from '../../auth';
import DownloadButton from '../../components/download_button';
import createDecorator from 'final-form-calculate';
import { getIn } from 'final-form';

// Relax MUI/untyped legacy components to `any` so their custom/v7 props
// type-check (behavior unchanged). Matches documents/ack_details.tsx.
const Grid: any = GridBase;
const Form: any = FormBase;
const Field: any = FieldBase;
const TextField: any = TextFieldBase;

const ReceiverShipping = 'Supplier';
const SenderShipping = 'Retailer';
const ManualShipping = 'Manual';

const StyledTable = styled(Table)({
    flex: '1',
    '& td': {
        padding: '5px'
    },
    '& th': {
        padding: '5px'
    }
});

const calculator = createDecorator(
{
    field: /OrderLines\[\d+\].ShipQuantity/,
    updates: (value: any, name: string, allValues: any) => {
        let weight = 0;
        let ret: Record<string, any> = {};
        let line = getIn(allValues, name.split('.').slice(0, -1).join('.'));
        if(!line.Weight){
            return ret;
        }
        allValues.OrderLines.forEach(line => {
            weight += convertToPounds((line.ShipQuantity || 0) * (line.Weight || 0), line.WeightUOM);
        });
        if((weight || weight === 0) && getIn(allValues, 'Weight') !== weight){
            ret['Weight'] = weight;
        }
        return ret;
    }
});

interface OrderFormProps {
    order?: any;
    setForm?: (ref: any) => void;
    onChange?: (order: any) => Promise<void> | void;
    classCodes?: any[];
}

const OrderForm = (props: OrderFormProps) => {
    const { classCodes, order, onChange, setForm } = props;
    const [errors, setErrors] = useState<string[]>([]);
    const [showMessage, setShowMessage] = useState(false);

    const handleSubmit = async (order: any) => {
        delete order.ShipmentInfos;
        var shipment = Object.assign({}, order);
        var cancellation = Object.assign({}, order);
        delete shipment.OrderLines;
        delete cancellation.OrderLines;
        shipment.ShipmentLines = [];
        cancellation.AckLines = [];
        cancellation.AcknowledgementNumber = "ACK_" + (order.OrderNumber || order.PartnerPO);
        shipment.ShipmentNumber = shipment.PartnerPO;
        for(const item of order.OrderLines){
            if(item.ShipQuantity > 0){
                let shipItem = Object.assign(item, {});
                shipItem.Quantity = item.ShipQuantity;
                shipment.ShipmentLines.push(shipItem);
            }
            if(item.CancelQuantity > 0){
                var ackItem = Object.assign(item, {});
                delete ackItem.ShipmentInfos;
                ackItem.Quantity = item.Quantity;
                ackItem.QuantityCancelled = item.CancelQuantity;
                ackItem.ChangeReason = item.CancelReason;
                cancellation.AckLines.push(ackItem);
            }
        }

        var errors = [];

        if(shipment.ShipmentLines.length === 0 && cancellation.AckLines.length === 0){
            setErrors(["You must ship or cancel at least one item."]);
            setShowMessage(true);
            return;
        }

        if(shipment.ShipmentLines.length > 0){
            try{
                let info: Record<string, any> = {
                    TrackingNumber: order.TrackingNumber,
                    Weight: order.Weight,
                    ClassCode: order.ShippingMethod,
                    CarrierCode: order.ShippingMethod,
                    DateShipped: moment().format("YYYY-MM-DD")
                };
                let trackingLabel: Record<string, any> = {};
                if(order.ShippingOption === SenderShipping || order.ShippingOption === ReceiverShipping){
                    trackingLabel = (await axios.post(Config.api + `/api/v1/orders/${order.Identifier.LogicbrokerKey}/TrackingLabel?useSenderAccount=${order.ShippingOption === SenderShipping}`, {
                        Package: {
                            Weight: info.Weight,
                            ClassCode: info.ClassCode,
                            WeightUnit: "lb",
                            DimensionUnit: "in"
                        },
                        ShipFromAddress: order.ShipFromAddress,
                        Items: shipment.ShipmentLines.map(r => ({LineNumber: r.LineNumber, SupplierSKU: r.ItemIdentifier.SupplierSKU, Quantity: r.Quantity}))
                    })).data;
                    info.TrackingNumber = trackingLabel.TrackingNumber;
                    info.ExtendedAttributes = [{Name: "TrackingLabel", Value: trackingLabel.Link}];
                }
                for(const item of shipment.ShipmentLines){
                    item.ShipmentInfos = [{...info, Qty: item.Quantity}];
                }
                let resp = (await axios.post(Config.api + '/api/v1/shipments', shipment, {headers: {SourceSystem: "Portal"}})).data;
                order.Shipments.push({id: resp.Body.LogicbrokerKey, tracking: trackingLabel.Link});
                order.TrackingNumber = '';
                order.OrderLines.forEach(item => {
                    if(item.ShipQuantity > 0){
                        item.QuantityRemaining -= item.ShipQuantity;
                        item.ShipQuantity = '';
                    }
                });
                if(trackingLabel.Link){
                    var attachmentPartner = order.ReceiverCompanyId === parseInt(Auth.getAccountNumber()) ? order.SenderCompanyId : order.ReceiverCompanyId;
                    let attachUrl = Config.api + `/api/v1/attachments`;
                    let axiosData = {
                        params: {
                            logicbrokerKey: resp.Body.LogicbrokerKey,
                            type: "pdf",
                            receiverId: attachmentPartner,
                            url: trackingLabel.Link,
                            description: `Tracking ${trackingLabel.TrackingNumber}`
                        }
                    };
                    try {
                        await axios.post(attachUrl, null, axiosData);
                    } catch(e) {
                        try {
                            await axios.post(attachUrl, null, axiosData);
                        }catch{
                            // do nothing, URL is saved to a KVP
                        }
                    }
                }
            }catch(e: any){
                if(e.response && e.response.data){
                    errors.push("Error creating shipment: " + new Helper().getApiErrors(e.response.data).join("\n"));
                } else {
                    errors.push("Error creating shipment: " + e.message);
                }
            }
        }
        if(cancellation.AckLines.length > 0){
            try{
                let resp = (await axios.post(Config.api + '/api/v1/acknowledgements', cancellation, {headers: {SourceSystem: "Portal"}})).data;
                order.Cancellations.push(resp.Body.LogicbrokerKey);
                order.OrderLines.forEach(item => {
                    if(item.CancelQuantity > 0){
                        item.QuantityRemaining -= item.CancelQuantity;
                        item.CancelQuantity = '';
                    }
                });
            }catch(e: any){
                if(e.response && e.response.data){
                    errors.push("Error creating cancellation: " + new Helper().getApiErrors(e.response.data).join("\n"));
                }
            }
        }
        order.OrderLines.forEach(item => {
            if(item.QuantityRemaining < 0){
                item.QuantityRemaining = 0;
            }
        });
        order.submitted = true;
        await onChange(order);
        setErrors(errors);
        setShowMessage(true);
    }

    const hasQuantityRemaining = (order: any) => {
        return order.OrderLines.filter(r => r.QuantityRemaining > 0).length > 0;
    }

    const handleContinue = async () => {
        let newOrder = {...order};
        newOrder.submitted = false;
        await onChange(newOrder);
        setShowMessage(false);
        setErrors([]);
    }

    if(!order){
        return <ContentLoader preserveAspectRatio='none' style={{height: '8em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>;
    }

    if(showMessage){
        return (<Card>
            <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Order {order.PartnerPO} placed on</span><Moment format='MM/DD/YYYY'>{order.OrderDate || order.DocumentDate}</Moment></Toolbar>
            <CardContent>
                <Grid container spacing={2} style={{textAlign: 'left'}}>
                    {(order.Shipments || []).map(s => <Grid size={{ xs: 12, sm: 12, md: 12 }} key={s.id}>Created shipment <DocumentLink type='Shipment' id={s.id}>#{s.id}</DocumentLink> {s.tracking ? <span> with <Link className='link' color='initial' href={s.tracking} target='_blank'>tracking label</Link></span> : ''}</Grid>)}
                    {(order.Cancellations || []).map(s => <Grid size={{ xs: 12, sm: 12, md: 12 }} key={s}>Created cancellation <DocumentLink type='Acknowledgement' id={s}>#{s}</DocumentLink></Grid>)}
                    {(errors || []).map((e, index) => <Grid size={{ xs: 12, sm: 12, md: 12 }} key={index}>{e}</Grid>)}
                    {(errors.length > 0 || hasQuantityRemaining(order)) && <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{textAlign:  'center'}}>
                        <Button variant='outlined' onClick={handleContinue}>{errors.length > 0 ? 'Retry' : 'Continue'}</Button>
                    </Grid>}
                </Grid>
            </CardContent>
        </Card>);
    }

        return <Form
    onSubmit={handleSubmit}
    initialValues={order}
    mutators={{
      ...arrayMutators,
      updateAddress
    }}
    decorators={[calculator]}
    render={({ handleSubmit, pristine, invalid, values, form: {mutators} }) => {
      let shipFrom = (formatAddress(values.ShipFromAddress) || '').trim();
      return <form onSubmit={handleSubmit} ref={form => setForm(form)}>
      <Card>
          <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Order {order.PartnerPO} placed on</span><Moment format='MM/DD/YYYY'>{order.OrderDate || order.DocumentDate}</Moment></Toolbar>
          <CardContent>
          <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Ship To: {formatAddress(order.ShipToAddress)}</Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Bill To: {formatAddress(order.BillToAddress)}</Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Ship From: {shipFrom} <AddressSelect mutators={mutators} contact={values.ShipFromAddress} name='ShipFromAddress'/></Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex', flexWrap: 'wrap', gap: '1em'}}>
              <Field label='Shipment Date' name={`ShipmentDate`} type='date' component={TextField} />
              {order.ShippingOptions.length > 0 && <div style={{width: '10rem'}}>
                <Field component={FastSelect} style={{width: '100%'}} options={order.ShippingOptions.map(r => ({label: r, value: r}))} label='Shipping Account' name={`ShippingOption`}/>
              </div>}
              {values.ShippingOption === ManualShipping && <>
              <div style={{width: '16rem'}}>
                <Field component={FastSelect} style={{width: '100%'}} options={classCodes.map(r => ({label: r.Description, value: r.Carrier}))} label='Shipping Method' name={`ShippingMethod`}/>
              </div>
              <Field style={{width: '12rem'}} label='Tracking Number' name={`TrackingNumber`} type='text' component={TextField} />
              </>}
              <Field style={{width: '6rem'}} label='Weight' name={`Weight`} type='number' component={TextField} endAdornment={<InputAdornment position="end">lbs</InputAdornment>} />
              <Field style={{width: '12rem'}} label='Estimated Delivery Date' name={`ExpectedDeliveryDate`} type='date' component={TextField} />
              <HeaderAttributes itemStyle={{width: '10em'}}/>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex'}}>
          <StyledTable>
          <TableHead>
              <TableRow>
                  <TableCell style={{width: '5em'}}>Line</TableCell>
                  <TableCell style={{width: '12em'}}>SKU</TableCell>
                  <TableCell style={{width: '12em'}}>Partner SKU</TableCell>
                  <TableCell style={{width: '12em'}}>UPC</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell style={{width: '7em'}}>Quantity Remaining</TableCell>
                  <TableCell style={{width: '7em'}}>Ship Quantity</TableCell>
                  <TableCell style={{width: '7em'}}>Cancel Quantity</TableCell>
                  <TableCell style={{width: '20em'}}>Cancellation Reason</TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
          <FieldArray name={`OrderLines`}>
          {({fields}) =>
              fields.map((name, index) => <Fragment key={name}><TableRow>
              <TableCell>{fields.value[index].LineNumber}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.SupplierSKU}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.PartnerSKU}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.UPC}</TableCell>
              <TableCell>{fields.value[index].Description}</TableCell>
              <TableCell>{fields.value[index].QuantityRemaining}</TableCell>
              <TableCell><Field style={{width: '100%'}} name={`${name}.ShipQuantity`} inputProps={{min: 0, max: fields.value[index].Quantity}} component={TextField} type='number'/></TableCell>
              <TableCell><Field style={{width: '100%'}} name={`${name}.CancelQuantity`} inputProps={{min: 0, max: fields.value[index].Quantity}} component={TextField} type='number'/></TableCell>
              <TableCell><Field style={{width: '100%'}} name={`${name}.CancelReason`} component={TextField} type='text'/></TableCell>
              </TableRow>
              <ItemAttributes name={name} item={fields.value[index]}/>
          </Fragment>)
          }
          </FieldArray>
          </TableBody>
          </StyledTable>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}><DownloadButton variant='contained' color='primary' type='submit' disabled={invalid || (values.OrderLines.every(o => (!o.ShipQuantity || o.ShipQuantity <= 0) && (!o.CancelQuantity || o.CancelQuantity <= 0)))} onClick={handleSubmit}>Submit</DownloadButton></Grid>
          </Grid>
          </CardContent>
          </Card>
        </form>
    }}/>;
}

const initOrder = (order: any) => {
    order.ShipmentDate = new Date().toISOString().split("T")[0];
    order.TrackingNumber = '';
    order.Weight = 0;
    order.ShippingMethod = '';
    order.Shipments = [];
    order.Cancellations = [];
    if(order.ShipmentInfos && order.ShipmentInfos.length > 0 && order.ShipmentInfos[0]){
        if(order.ShipmentInfos[0].ReceiverClassCode){
            order.ShippingMethod = order.ShipmentInfos[0].ReceiverClassCode;
        } else if (order.ShipmentInfos[0].ClassCode){
            order.ShippingMethod = order.ShipmentInfos[0].ClassCode;
        }
    }
    for(var z = 0; z < order.OrderLines.length; z++){
        var item = order.OrderLines[z];
        if(item.LineNumber === null || typeof item.LineNumber === 'undefined'){
            item.LineNumber = z + 1;
        }
        item.ShipQuantity = '';
        item.CancelQuantity = '';
        item.CancelReason = '';
        if(!item.ExtendedAttributes){
            item.ExtendedAttributes = [];
        }
        var shipped = item.ExtendedAttributes.find(r => r.Name === 'OrderQuantityShipped');
        var cancelled = item.ExtendedAttributes.find(r => r.Name === 'QtyCancelled');
        item.QuantityRemaining = item.Quantity;
        if(shipped && shipped.Value){
            item.QuantityRemaining = item.QuantityRemaining - parseInt(shipped.Value);
        }
        if(cancelled && cancelled.Value){
            item.QuantityRemaining = item.QuantityRemaining - parseInt(cancelled.Value);
        }
        if(item.QuantityRemaining < 0){
            item.QuantityRemaining = 0;
        }

        if(item.QuantityRemaining > 0){
            item.ShipQuantity = item.QuantityRemaining.toString();
        }

        if(item.Weight){
            order.Weight += convertToPounds((item.Quantity || 0) * (item.Weight || 0), item.WeightUOM);
        }
    }
    if(!order.Weight){
        order.Weight = null;
    }
}

const ShipMultiple = (props: any) => {
    const [orders, setOrders] = useState<any[]>([]);
    const [classCodes, setClassCodes] = useState<Record<string, any>>({});
    const [ids, setIds] = useState<string[]>([]);
    const [showPackingSlip, setShowPackingSlip] = useState(false);
    const [submitting, setSubmitting] = useState<boolean | undefined>(undefined);
    const forms = useRef<Record<string, any>>({});

    useEffect(() => {
        const load = async () => {
            var ids: any = new Helper().queryString()['orderids'];
            if(ids){
                ids = ids.split(',');
            }
            if(!ids || ids.length === 0){
                //TODO: update state to show error
                return;
            }
            setIds(ids);
            let defaultAddresses = ((await axios.get(Config.api + `/odata/Company/DefaultAddresses?$filter=AddressType eq 'ShipFrom'`)).data || {}).value || [];
            for(var i = 0; i < ids.length; i++){
                let id = ids[i];
                let order = (await axios.get(Config.api + `/api/v1/orders/${id}`)).data.Body.SalesOrder;
                let ccs = await getClassCodes(order.SenderCompanyId);
                let shippingOptions = (await axios.get(Config.api + `/api/v1/orders/${id}/shippingoptions`)).data;
                order.ShippingOptions = [];
                if(shippingOptions.HasSenderAccount){
                    order.ShippingOptions.push(SenderShipping);
                }
                if(shippingOptions.HasReceiverAccount){
                    order.ShippingOptions.push(ReceiverShipping);
                }
                order.ShippingOptions.push(ManualShipping);
                order.ShippingOption = order.ShippingOptions[0];
                let defaultAddress = defaultAddresses.find(r => r.PartnerCoId === order.ReceiverCompanyId);
                if(!defaultAddress){
                    defaultAddress = defaultAddresses.find(r => r.PartnerCoId === null);
                }
                if(defaultAddress){
                    order.ShipFromAddress = {
                        CompanyName: defaultAddress.CompanyName,
                        FirstName: defaultAddress.FirstName,
                        LastName: defaultAddress.LastName,
                        Address1: defaultAddress.Address1,
                        Address2: defaultAddress.Address2,
                        City: defaultAddress.City,
                        State: defaultAddress.State,
                        Zip: defaultAddress.Zip,
                        Country: defaultAddress.Country,
                        CountryCode: defaultAddress.CountryCode,
                        Phone: defaultAddress.Phone,
                        Email: defaultAddress.Email,
                        FaxNumber: defaultAddress.FaxNumber,
                        AddressCode: defaultAddress.AddressCode
                    }
                }
                initOrder(order);
                // Restore the original atomic setState({orders, classCodes}): under the
                // app's legacy ReactDOM.render there is no async auto-batching, so
                // committing these separately let an order render with its classCodes entry
                // still undefined -> OrderForm's classCodes.map crashed on manual shipments.
                unstable_batchedUpdates(() => {
                    setOrders(prev => [...prev, order]);
                    setClassCodes(prev => ({...prev, [order.SenderCompanyId]: ccs}));
                });
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getShipmentIds = () => {
        return orders.filter(r => r.Shipments).map(r => r.Shipments.map(r => r.id)).reduce((a, b) => a.concat(b), []).join(",");
    }

    const updateOrder = async (order: any) => {
        setOrders(prev => {
            let orders = [...prev];
            let orderIndex = orders.findIndex(r => r.Identifier.LogicbrokerKey === order.Identifier.LogicbrokerKey);
            if(orderIndex !== -1){
                orders[orderIndex] = order;
            }
            return orders;
        });
    }

    const submitAll = async () => {
        setSubmitting(true);
        try{
            let ordersToSubmit = orders.filter(r => !r.submitted).map(r => r.Identifier.LogicbrokerKey);
            for(const id of ordersToSubmit){
                let form = forms.current[id];
                if(form){
                    await new Promise(resolve => {
                        setTimeout(resolve, 1000);
                      });
                    triggerSubmit(form);
                }
            }
        }catch{}
        setSubmitting(false);
    }

    const setForm = (id: any, ref: any) => {
        let order = orders.filter(r => r.Identifier.LogicbrokerKey === id)[0];
        if(!order || !ref){
            return;
        }
        forms.current[id] = ref;
    }

    var diff = ids.slice(orders.length);
    return <Grid container spacing={2}>
            {orders.map((o) => <Grid key={o.Identifier.LogicbrokerKey} size={{ xs: 12, sm: 12, md: 12 }}><OrderForm setForm={setForm.bind(null, o.Identifier.LogicbrokerKey)} onChange={updateOrder} order={o} classCodes={classCodes[o.SenderCompanyId]}/></Grid>)}
            {diff.map((d) => <Grid key={d + "-diff"} size={{ xs: 12, sm: 12, md: 12 }}><OrderForm /></Grid>)}
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <CardContent>
                        <Button variant='outlined' onClick={() => setShowPackingSlip(true)} disabled={orders.filter(r => r.Shipments && r.Shipments.length > 0).length === 0}>View all packing slips</Button>
                        <Button variant='contained' style={{marginLeft: '1em'}} color='primary' onClick={submitAll} disabled={submitting || orders.filter(r => !r.submitted).length === 0}>Submit all</Button>
                    </CardContent>
                </Card>
            </Grid>
            <LabelDialog open={showPackingSlip} onClose={() => setShowPackingSlip(false)} fileName={`shipmentsPackingSlip-${Math.floor(Date.now() / 1000).toString()}`} path={`/api/v1/shipments/packingslip?logicbrokerkeys=${getShipmentIds()}`}/>
        </Grid>;
}

export default ShipMultiple;
